/**
 * Roller - Vertical cycling animation helper
 *
 * Manages cycling animation for an existing roller element.
 * Expects structure: roller > [item, item, item, ...]
 * Last item should duplicate first for seamless looping.
 *
 * Usage:
 *   const roller = new Roller(rollerElement, {
 *     initialDelay: 5000,    // Delay before first roll
 *     displayDuration: 2000, // How long each item stays visible
 *   });
 *   roller.start();
 */
export default class Roller {
  constructor(element, options = {}) {
    this.element = element;
    this.initialDelay = options.initialDelay ?? 5000;
    this.displayDuration = options.displayDuration ?? 2000;
    this.onRoll = options.onRoll || null;

    this.currentPosition = 0;
    this.intervalId = null;
    this.timeoutId = null;
    this.isRunning = false;

    // Count items in the roller
    this.totalItems = this.element.children.length;
  }

  /**
   * Apply dynamic sizing based on item count
   */
  applyDynamicSizing() {
    if (this.totalItems === 0) return;

    const itemHeight = 100 / this.totalItems;

    // Set roller height
    this.element.style.height = `${this.totalItems * 100}%`;

    // Set each item's flex
    Array.from(this.element.children).forEach(item => {
      item.style.flex = `0 0 ${itemHeight}%`;
    });
  }

  /**
   * Start the rolling cycle
   */
  start() {
    if (this.isRunning || this.totalItems < 2) return;

    this.applyDynamicSizing();
    this.isRunning = true;
    this.currentPosition = 0;

    this.timeoutId = setTimeout(() => {
      this.rollNext();
      this.intervalId = setInterval(() => this.rollNext(), this.displayDuration);
    }, this.initialDelay);
  }

  /**
   * Stop the rolling cycle
   */
  stop() {
    this.isRunning = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Roll to the next position
   */
  rollNext() {
    this.currentPosition++;

    if (this.currentPosition >= this.totalItems) {
      // We've rolled to the duplicate - reset instantly
      this.reset();
      this.currentPosition = 0;
    } else {
      this.rollTo(this.currentPosition);
    }

    if (this.onRoll) {
      this.onRoll(this.currentPosition);
    }
  }

  /**
   * Roll to a specific position
   */
  rollTo(position) {
    const itemHeight = 100 / this.totalItems;
    const offset = position * itemHeight;
    this.element.style.transform = `translateY(-${offset}%)`;
  }

  /**
   * Instantly reset to position 0 without transition
   */
  reset() {
    this.element.style.transition = "none";
    this.element.style.transform = "translateY(0)";
    // Force reflow then restore transition
    this.element.offsetHeight;
    this.element.style.transition = "";
  }

  /**
   * Pause rolling and reset to first item
   */
  pause() {
    this.stop();
    this.reset();
    this.currentPosition = 0;
  }

  /**
   * Resume rolling from current position
   */
  resume() {
    if (this.isRunning || this.totalItems < 2) return;

    this.isRunning = true;
    this.intervalId = setInterval(() => this.rollNext(), this.displayDuration);
  }
}
